import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { ChatPage } from './pages/ChatPage';
import { MyWorkPage } from './pages/MyWorkPage';
import { AttentionQueuePage } from './pages/AttentionQueuePage';
import { BusinessHealthPage } from './pages/BusinessHealthPage';
import { AdminPage } from './pages/AdminPage';
import { SettingsPage } from './pages/SettingsPage';
import { VendorsPage } from './pages/VendorsPage';
import { OwnerNotesPage } from './pages/OwnerNotesPage';
import { LibraryPage } from './pages/LibraryPage';
import { HrPage } from './pages/hr/HrPage';
import { HrAccessProvider } from './pages/hr/HrAccessContext';
import { HrRequestsProvider } from './pages/hr/HrRequestsContext';
import { MyHrViewerRoleProvider } from './pages/hr/MyHrViewerRoleContext';
import { WorkGroupsPage } from './pages/sharedTasks/WorkGroupsPage';
import { AdminAreaTabs } from './pages/sharedTasks/AdminAreaTabs';
import { SimulatedPlaceholderPage } from './pages/sharedTasks/SimulatedPlaceholderPage';
import { LOCAL_PILOT } from './pages/sharedTasks/localPilot';

// BUILD-043: Vendors moved out of Settings to the dedicated /vendors page. The old /settings?tab=vendors link (bookmarks, PM's "Manage vendors in UniqueHQ" link) resolves to the
// new page; every other Settings tab and its ?tab= links are untouched.
function SettingsRoute() {
  const [params] = useSearchParams();
  if (params.get('tab') === 'vendors') return <Navigate to="/vendors" replace />;
  return <SettingsPage />;
}

function AppRoutes() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-navy">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    // UAT visual cleanup (2026-10-05): HrAccessProvider wraps AppLayout (not just HrPage) so the
    // sidebar's HR dropdown can read the caller's resolved surfaces (myHr/myTeam/hrAdmin) to
    // decide which sub-items to show, using the exact same GET /hr/me resolution HrPage itself
    // uses — one shared source, not two separate calls computing the same thing.
    <HrAccessProvider>
      {/* BUILD-042 My HR self-service/change-request decision (2026-10-06): a single shared,
          session-only correction-request store, read by both My HR's "Request a correction"
          cards and HR Administration's reviewer mockup — see HrRequestsContext.tsx's header for
          why this is a local demonstration, not a real backend. */}
      <HrRequestsProvider>
      {/* "Simulate each view for me" (2026-10-07): moved up to wrap AppLayout (not just My HR)
          so the toggle itself can live in AppLayout's top header bar instead of the My HR page
          body — state persists even if Chris navigates away and back to My HR. */}
      <MyHrViewerRoleProvider>
      <AppLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/my-work" element={<MyWorkPage />} />
          <Route path="/attention" element={<AttentionQueuePage />} />
          <Route path="/knowledge" element={<ChatPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/business-health" element={<BusinessHealthPage />} />
          <Route path="/hr/*" element={<HrPage />} />
          {/* BUILD-046 (pass F1): Work groups is a tab of HQ administration. The strip is drawn only for a person who may see both pages AND only when the Pilot routes answer (GET /work/me), so with the
              Pilot off nothing here changes. A convenience, not a control: the server authorizes every page and request. */}
          <Route path="/admin" element={<><AdminAreaTabs /><AdminPage /></>} />
          <Route path="/owner/notes" element={<OwnerNotesPage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/work-groups" element={<><AdminAreaTabs /><WorkGroupsPage /></>} />
          {/* BUILD-046 8.14: the stand-in page for the local pilot's simulated source and evidence links; absent from any shipped build (LOCAL_PILOT is statically false there). */}
          {LOCAL_PILOT && <Route path="/simulated/*" element={<SimulatedPlaceholderPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
      </MyHrViewerRoleProvider>
      </HrRequestsProvider>
    </HrAccessProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
